import { useCallback, useRef } from 'react';
import type { ImageViewerAction } from '../imageViewerReducer';

const WHEEL_ZOOM_FACTOR = 0.1;
const MIN_PINCH_DISTANCE = 10;

interface UseImageZoomOptions {
  dispatch: React.Dispatch<ImageViewerAction>;
  getState: () => { zoom: number; panX: number; panY: number; fitZoom: number };
  viewportRef: React.RefObject<HTMLElement>;
  disabled?: boolean;
}

function getTouchDistance(t1: React.Touch, t2: React.Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function getTouchCenter(t1: React.Touch, t2: React.Touch): { x: number; y: number } {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  };
}

export function useImageZoom({ dispatch, getState, viewportRef, disabled }: UseImageZoomOptions) {
  const pinchRef = useRef<{ distance: number; zoom: number; panX: number; panY: number } | null>(null);

  /** Zoom toward a point (in viewport coordinates) */
  const zoomToward = useCallback(
    (clientX: number, clientY: number, newZoom: number) => {
      const vp = viewportRef.current;
      if (!vp) return;
      const rect = vp.getBoundingClientRect();
      const state = getState();

      // Point in viewport space
      const vpX = clientX - rect.left;
      const vpY = clientY - rect.top;

      // Point in image space (before zoom)
      const imgX = (vpX - state.panX) / state.zoom;
      const imgY = (vpY - state.panY) / state.zoom;

      // After zoom, keep the same image point under the cursor
      const panX = vpX - imgX * newZoom;
      const panY = vpY - imgY * newZoom;

      dispatch({ type: 'SET_ZOOM', zoom: newZoom, panX, panY });
    },
    [dispatch, getState, viewportRef],
  );

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      if (disabled) return;
      e.preventDefault();
      const state = getState();
      const factor = e.deltaY > 0 ? 1 - WHEEL_ZOOM_FACTOR : 1 + WHEEL_ZOOM_FACTOR;
      const newZoom = state.zoom * factor;
      zoomToward(e.clientX, e.clientY, newZoom);
    },
    [disabled, getState, zoomToward],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;
      if (e.touches.length === 2) {
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        const state = getState();
        pinchRef.current = { distance: dist, zoom: state.zoom, panX: state.panX, panY: state.panY };
      }
    },
    [disabled, getState],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || !pinchRef.current || e.touches.length !== 2) return;
      e.preventDefault();
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      if (dist < MIN_PINCH_DISTANCE) return;

      const ratio = dist / pinchRef.current.distance;
      const newZoom = pinchRef.current.zoom * ratio;
      const center = getTouchCenter(e.touches[0], e.touches[1]);
      zoomToward(center.x, center.y, newZoom);
    },
    [disabled, zoomToward],
  );

  const onTouchEnd = useCallback(() => {
    pinchRef.current = null;
  }, []);

  const onDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      const state = getState();
      // Toggle between fit and 100%
      const target = Math.abs(state.zoom - 1) < 0.01 ? state.fitZoom : 1;
      zoomToward(e.clientX, e.clientY, target);
    },
    [disabled, getState, zoomToward],
  );

  return { onWheel, onTouchStart, onTouchMove, onTouchEnd, onDoubleClick, zoomToward };
}
